/**
 * useSnapshotActions — wraps mutations with confirmation + toast UX
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback } from 'react';
import {
  matchTracker,
  type TrackerId,
  type VoluumFlowSnapshot,
} from '@trafficpulse/shared';

import { getApiErrorMessage } from '@/lib/api-error';
import { queueRestoreProviderOf } from '@/features/trackers/flow-optimizer';
import { useToast } from '@/providers/ToastProvider';
import {
  useDeleteFlowSnapshot,
  useRestoreFlowSnapshot,
  useTakeFlowSnapshot,
} from '../hooks';
export interface UseSnapshotActionsParams {
  flowId: string;
  provider?: TrackerId;
  /** Called once a restore landed (e.g. to refetch a page's own flow query). */
  onRestored?: () => void;
}

export function useSnapshotActions({
  flowId,
  provider = 'voluum',
  onRestored,
}: UseSnapshotActionsParams) {
  const toast = useToast();
  const take = useTakeFlowSnapshot(flowId, provider);
  const restore = useRestoreFlowSnapshot(flowId, provider);
  const remove = useDeleteFlowSnapshot(flowId, provider);

  const onTake = useCallback(
    async (label?: string) => {
      try {
        await take.mutateAsync(label);
        toast.success('Snapshot saved');
      } catch (err) {
        toast.error('Failed to take snapshot', readError(err, provider));
      }
    },
    [take, toast, provider],
  );

  const onRestore = useCallback(
    async (snap: VoluumFlowSnapshot) => {
      if (
        !window.confirm(
          'Restore this snapshot? This will replace the live flow.',
        )
      )
        return;
      const syncQueue = askSyncQueue(provider);
      try {
        await restore.mutateAsync({ snapshotId: snap.id, syncQueue });
        toast.success('Snapshot restored', 'Live flow updated.');
        onRestored?.();
      } catch (err) {
        toast.error('Failed to restore', readError(err, provider));
      }
    },
    [restore, toast, provider, onRestored],
  );

  const onDelete = useCallback(
    async (snap: VoluumFlowSnapshot) => {
      if (!window.confirm('Delete this snapshot?')) return;
      try {
        await remove.mutateAsync(snap.id);
        toast.success('Snapshot deleted');
      } catch (err) {
        toast.error('Failed to delete', readError(err, provider));
      }
    },
    [remove, toast, provider],
  );

  return {
    onTake,
    onRestore,
    onDelete,
    busy: take.isPending || restore.isPending || remove.isPending,
    taking: take.isPending,
  };
}

/**
 * The bank/queue follows the flow by default (recommended). Elements the
 * restore removes keep their test history — recorded, never wiped. A tracker
 * without the bank-backed queue has nothing to sync: no question asked.
 */
function askSyncQueue(provider: TrackerId): boolean {
  if (!queueRestoreProviderOf(provider)) return false;
  return window.confirm(
    'Also sync the queue/bank to the restored flow? (Recommended)\n' +
      'OK = sync (test history is preserved) · Cancel = restore the flow only.',
  );
}

/**
 * Voluum / Binom: the error text, as always. Tres Commas: the backend's own
 * reason, so a refused write says why (writes off, flow not allow-listed).
 */
function readError(err: unknown, provider: TrackerId): string {
  const plain = err instanceof Error ? err.message : 'Unknown error';
  return matchTracker(provider, {
    voluum: () => plain,
    binom: () => plain,
    'tres-commas': () => getApiErrorMessage(err, plain),
  });
}
